import Space from 'antd/lib/space';
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import { ReleaseComponentProps } from '../../typescriptHelpers';
import Switch from 'antd/lib/switch';
import { useDispatch, useSelector } from 'react-redux';
import { clearDemoModeForReleases, setDemoReleases } from '../../actions/ui';
import { antGrey, colorWithAlpha, runwayColors, rwColors } from '../styles/globalStyles';
import { RootState } from '../../reducers/reducers';
import {
    selectAppPlatform,
    selectApp,
    selectIsDemoModeEnabledForRelease,
    selectRelease,
    selectReleasePilotEligibleUsersForApp,
    selectAllTrackingPropertiesForRelease,
    selectAppSettings,
    selectUser,
    selectAllReleaseSteps,
} from '../../selectors/selectors';
import * as AnalyticEvents from '../../utils/analytics';
import { massageTestReleaseData } from '../../helpers/demoHelpers';
import { FeatureFlag, Release, ReleaseStep, RolloutMonitoring } from '../../types';
import { baseRolloutData } from '../../testData/rollout';
import { generateFeatureFlags } from '../../testData/featureFlags';
import { mockReleaseForPlatform } from '@/testData/liveRelease';
import { baseReleaseStepsForPlatform } from '@/testData/releaseSteps';

export enum ReleaseDemoBannerPlacement {
    ReleaseStep = 'releaseStep',
    Overview = 'overview',
}

interface ReleaseDemoViewProps extends ReleaseComponentProps {
    placement?: ReleaseDemoBannerPlacement;
}

const ReleaseDemoView: React.FC<ReleaseDemoViewProps> = (props: ReleaseDemoViewProps) => {
    const dispatch = useDispatch();
    const isDemoModeEnabled = useSelector((state: RootState) =>
        selectIsDemoModeEnabledForRelease(state, props.releaseId),
    );
    const platform = useSelector((state: RootState) => selectAppPlatform(state, props.appId));
    const currentRelease = useSelector((state: RootState) => selectRelease(state, props.releaseId));
    const currentReleaseSteps = useSelector((state: RootState) => selectAllReleaseSteps(state));

    const currentUser = useSelector((state: RootState) => selectUser(state));

    const defaultReleaseType = useSelector(
        (state: RootState) => selectAppSettings(state, props.appId).defaultReleaseType,
    );
    const appUsesStaticReleaseBranchForNonHotfixes = useSelector(
        (state: RootState) => selectApp(state, props.appId).appSettings.usesStaticReleaseBranchForNonHotfixes,
    );
    const randomReleasePilotUser = useSelector(
        (state: RootState) => selectReleasePilotEligibleUsersForApp(state, props.appId)[0],
    );

    const allReleaseTrackingProperties = useSelector((state: RootState) =>
        selectAllTrackingPropertiesForRelease(state, props.orgId, props.appId, props.releaseId),
    );

    const updateDemoModeEnabled = (checked) => {
        if (checked) {
            const testReleaseBaseMock = mockReleaseForPlatform(platform) as Release;

            const steps = baseReleaseStepsForPlatform(platform) as ReleaseStep[];

            const featureFlags = generateFeatureFlags(platform);

            const { testRelease, testReleaseSteps } = massageTestReleaseData(
                platform,
                testReleaseBaseMock,
                steps,
                appUsesStaticReleaseBranchForNonHotfixes,
                featureFlags as FeatureFlag[],
                defaultReleaseType,
                currentRelease,
                currentReleaseSteps,
                randomReleasePilotUser?.id,
                currentUser,
            );

            dispatch(
                setDemoReleases({
                    demoReleases: [
                        {
                            releaseId: props.releaseId,
                            release: testRelease,
                            rolloutMonitoring: baseRolloutData(platform) as RolloutMonitoring,
                            releaseSteps: testReleaseSteps,
                        },
                    ],
                }),
            );

            AnalyticEvents.track(AnalyticEvents.Event.ViewReleaseDemo, allReleaseTrackingProperties);
        } else {
            dispatch(clearDemoModeForReleases({ releaseIds: [props.releaseId] }));
        }
    };

    const titleText =
        isDemoModeEnabled ? '🔮 You are viewing this release in demo mode 🔮' : '✨ View this release in demo mode ✨';

    const descText =
        isDemoModeEnabled ?
            'Data shown is for illustrative purposes only. All actions are disabled (read-only).'
        :   `${
                props.placement === ReleaseDemoBannerPlacement.Overview ?
                    'Enable demo mode to see how this view would look with all integrations connected.'
                :   'Enable demo mode to see how this step would look with all integrations connected.'
            } Demo mode is read-only - all actions on the release will be disabled.`;

    return (
        <Space
            direction="vertical"
            style={{
                padding: 16,
                backgroundColor: colorWithAlpha(rwColors.primary, isDemoModeEnabled ? 0.85 : 0.2),
                borderRadius: 8,
                flexGrow: 1,
                marginBottom: 18,
            }}
        >
            <Text strong style={{ fontSize: 15, color: isDemoModeEnabled ? 'white' : runwayColors.black }}>
                {titleText}
            </Text>
            <Text style={{ color: isDemoModeEnabled ? 'white' : runwayColors.black }}>{descText}</Text>
            <Switch
                checked={isDemoModeEnabled}
                onChange={(checked) => updateDemoModeEnabled(checked)}
                style={{
                    backgroundColor: isDemoModeEnabled ? 'var(--rw-demo-mode-bg)' : antGrey.grey5,
                }}
            ></Switch>
        </Space>
    );
};

export default ReleaseDemoView;
